import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiFolder, FiGrid, FiList, FiPlus, FiEdit2, FiTrash2, FiUsers, FiDownload, FiUpload, FiCheck } from 'react-icons/fi';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { foldersService } from '../../services/folders.service';
import PermissionsModal from './components/PermissionsModal';
import { useTheme } from 'styled-components';
import {
  Container,
  Header,
  Title,
  HeaderActions,
  FilterToggle,
  FilterButton,
  ViewToggle,
  ViewButton,
  ActionButton,
  FoldersContainer,
  FolderCard,
  FolderIcon,
  FolderInfo,
  FolderName,
  FolderDetails,
  FolderActions,
  ActionIcon,
  LoadingContainer,
  EmptyState,
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalCloseButton,
  ModalBody,
  FormGroup,
  FormLabel,
  FormInput,
  ModalFooter,
  CancelButton,
  ConfirmButton,
  PermissionsList,
  PermissionItem,
  UserName,
  PermissionLevel,
  SearchContainer,
  SearchInput,
  PaginationContainer,
  PaginationInfo,
  PaginationButtons,
  PaginationButton,
  ErrorMessage,
  SuccessMessage
} from './styles';

interface Folder {
  id: string;
  name: string;
  description?: string;
  createdAt: string;
  createdBy: string;
  fileCount: number;
  permissions: Array<{
    userId: string;
    userName: string;
    level: 'read' | 'write' | 'admin';
  }>;
}

type ViewMode = 'grid' | 'list';
type ModalType = 'create' | 'edit' | 'delete' | 'permissions' | null;

const Folders: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { user } = useAuth();
  
  const [folders, setFolders] = useState<Folder[]>([]);
  const [filteredFolders, setFilteredFolders] = useState<Folder[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [selectedFolder, setSelectedFolder] = useState<Folder | null>(null);
  const [modalType, setModalType] = useState<ModalType>(null);
  const [filterMode, setFilterMode] = useState<'all' | 'my-folders' | 'accessible'>('my-folders');
  const [uploadingTo, setUploadingTo] = useState<string | null>(null);
  const [mensagem, setMensagem] = useState<{ type: 'error' | 'success'; text: string } | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12); // 12 pastas por página
  
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });

  useEffect(() => {
    loadFolders();
  }, []);

  useEffect(() => {
    loadFolders(filterMode);
  }, [filterMode]);

  // Função para verificar permissões do usuário atual em uma pasta
  const getUserPermissionForFolder = (folder: Folder): 'read' | 'write' | 'admin' | null => {
    if (!user) return null;
    
    const userPermission = folder.permissions.find(p => p.userId === user.id);
    return userPermission ? userPermission.level : null;
  };

  // Verificar se usuário pode fazer upload em uma pasta
  const canUploadToFolder = (folder: Folder): boolean => {
    const permission = getUserPermissionForFolder(folder);
    return permission === 'write' || permission === 'admin';
  };

  // Verificar se usuário pode deletar uma pasta  
  const canDeleteFolder = (folder: Folder): boolean => {
    const permission = getUserPermissionForFolder(folder);
    return permission === 'admin';
  };

  // Verificar se usuário pode editar uma pasta
  const canEditFolder = (folder: Folder): boolean => {
    const permission = getUserPermissionForFolder(folder);
    return permission === 'write' || permission === 'admin';
  };

  // Verificar se usuário pode gerenciar permissões de uma pasta
  const canManagePermissions = (folder: Folder): boolean => {
    const permission = getUserPermissionForFolder(folder);
    return permission === 'write' || permission === 'admin';
  };

  const loadFolders = async (mode: 'all' | 'my-folders' | 'accessible' = filterMode) => {
    try {
      setLoading(true);
      console.log('Carregando pastas...', mode);
      
      let response;
      
      switch (mode) {
        case 'my-folders':
          response = await foldersService.getMyFolders();
          break;
        case 'accessible':
          response = await foldersService.getFoldersWithFilter('accessible');
          break;
        case 'all':
        default:
          response = await foldersService.getFolders();
          break;
      }
      
      console.log('Resposta do serviço:', response);
      console.log('Pastas a serem definidas:', response.data);
      setFolders(response.data || []);
      setFilteredFolders(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar pastas:', error);
      setFolders([]); // Definir array vazio em caso de erro
      setFilteredFolders([]);
    } finally {
      setLoading(false);
    }
  };

  // Função de busca
  const handleSearch = (term: string) => {
    setSearchTerm(term);
    setCurrentPage(1); // Reset para primeira página ao buscar
    
    if (!term.trim()) {
      setFilteredFolders(folders);
      return;
    }

    const filtered = folders.filter(folder =>
      folder.name.toLowerCase().includes(term.toLowerCase()) ||
      folder.description?.toLowerCase().includes(term.toLowerCase())
    );
    setFilteredFolders(filtered);
  };

  // Aplicar filtro quando folders mudarem
  React.useEffect(() => {
    handleSearch(searchTerm);
  }, [folders]);

  // Calcular paginação
  const totalPages = Math.ceil(filteredFolders.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedFolders = filteredFolders.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const generatePageNumbers = () => {
    const pages: number[] = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      const halfVisible = Math.floor(maxVisiblePages / 2);
      let startPage = Math.max(1, currentPage - halfVisible);
      let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
      
      if (endPage - startPage < maxVisiblePages - 1) {
        startPage = Math.max(1, endPage - maxVisiblePages + 1);
      }
      
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
    }
    
    return pages;
  };

  const handleCreateFolder = () => {
    setFormData({ name: '', description: '' });
    setSelectedFolder(null);
    setModalType('create');
  };

  const handleEditFolder = (folder: Folder) => {
    setFormData({ name: folder.name, description: folder.description || '' });
    setSelectedFolder(folder);
    setModalType('edit');
  };

  const handleDeleteFolder = (folder: Folder) => {
    setSelectedFolder(folder);
    setModalType('delete');
  };

  const handleManagePermissions = (folder: Folder) => {
    setSelectedFolder(folder);
    setModalType('permissions');
  };

  const handleOpenFolder = (folder: Folder) => {
    navigate(`/folders/${folder.id}`);
  };

  const handleSubmitForm = async () => {
    try {
      if (modalType === 'create') {
        await foldersService.createFolder(formData);
      } else if (modalType === 'edit' && selectedFolder) {
        await foldersService.updateFolder(selectedFolder.id, formData);
      } else if (modalType === 'delete' && selectedFolder) {
        await foldersService.deleteFolder(selectedFolder.id);
      }
      
      await loadFolders();
      handleCloseModal();
    } catch (error) {
      console.error('Erro ao processar pasta:', error);
    }
  };

  const handleCloseModal = () => {
    setModalType(null);
    setSelectedFolder(null);
    setFormData({ name: '', description: '' });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  /**
   * Mostra mensagem ao usuário. Erro fica mais tempo na tela que sucesso: quem precisa ler e agir
   * é quem levou o erro.
   */
  const mostrarMensagem = (type: 'error' | 'success', text: string) => {
    setMensagem({ type, text });
    setTimeout(() => setMensagem(null), type === 'error' ? 10000 : 4000);
  };

  const handleQuickUpload = async (folderId: string, files: FileList | null) => {
    if (!files || files.length === 0) return;

    setUploadingTo(folderId);

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];

        /*
         * Valida ANTES de enviar. Sem isto o usuário espera o upload de um arquivo grande para só
         * então descobrir que o tipo ou o tamanho não serve.
         */
        const validacao = foldersService.validateFile(file);
        if (!validacao.isValid) {
          throw new Error(`${file.name}: ${validacao.error}`);
        }

        await foldersService.uploadFile(folderId, file);
      }

      mostrarMensagem(
        'success',
        `${files.length} arquivo${files.length !== 1 ? 's' : ''} enviado${files.length !== 1 ? 's' : ''} com sucesso.`
      );
      await loadFolders();
    } catch (error: any) {
      /*
       * ANTES este catch fazia apenas `console.error` — falha de upload era INVISÍVEL para o
       * usuário, que via o spinner parar e nada acontecer. É a dor #1 registrada no projeto, e foi
       * o que fez um apagão de OCR durar 5 meses sem ninguém notar.
       */
      const detalhe =
        error?.response?.data?.message ?? error?.message ?? 'Erro desconhecido ao enviar o arquivo.';
      console.error('Erro no upload:', error);
      mostrarMensagem('error', `Não foi possível enviar: ${detalhe}`);
    } finally {
      setUploadingTo(null);
    }
  };

  if (loading) {
    return (
      <Container>
        <LoadingContainer>
          <div>Carregando pastas...</div>
        </LoadingContainer>
      </Container>
    );
  }

  return (
    <Container>
      {mensagem && (
        <div style={{ position: 'fixed', top: '20px', right: '20px', zIndex: 1001, maxWidth: '400px' }}>
          {mensagem.type === 'error' ? (
            <ErrorMessage>{mensagem.text}</ErrorMessage>
          ) : (
            <SuccessMessage>{mensagem.text}</SuccessMessage>
          )}
        </div>
      )}

      <Header>
        <Title>Pastas</Title>
        <HeaderActions>
          <FilterToggle>
            <FilterButton 
              active={filterMode === 'my-folders'} 
              onClick={() => setFilterMode('my-folders')}
            >
              Minhas Pastas
            </FilterButton>
            <FilterButton 
              active={filterMode === 'accessible'} 
              onClick={() => setFilterMode('accessible')}
            >
              Acessíveis
            </FilterButton>
            {/* <FilterButton 
              active={filterMode === 'all'} 
              onClick={() => setFilterMode('all')}
            >
              Todas
            </FilterButton> */}
          </FilterToggle>
          
          <ViewToggle>
            <ViewButton 
              active={viewMode === 'grid'} 
              onClick={() => setViewMode('grid')}
            >
              {FiGrid({ size: 18 })}
            </ViewButton>
            <ViewButton 
              active={viewMode === 'list'} 
              onClick={() => setViewMode('list')}
            >
              {FiList({ size: 18 })}
            </ViewButton>
          </ViewToggle>
          <ActionButton onClick={handleCreateFolder}>
            {FiPlus({ size: 18 })}
            Nova Pasta
          </ActionButton>
        </HeaderActions>
      </Header>

      <SearchContainer>
        <SearchInput
          type="text"
          placeholder="Buscar pastas por nome ou descrição..."
          value={searchTerm}
          onChange={(e) => handleSearch(e.target.value)}
        />
      </SearchContainer>

      {filteredFolders.length === 0 ? (
        <EmptyState>
          {FiFolder({ size: 64 })}
          <h3>
            {searchTerm && folders.length > 0 ? 'Nenhuma pasta encontrada' :
             filterMode === 'my-folders' ? 'Você ainda não tem pastas' : 
             filterMode === 'accessible' ? 'Nenhuma pasta acessível' : 
             'Nenhuma pasta encontrada'}
          </h3>
          <p>
            {searchTerm && folders.length > 0 ? 'Tente ajustar os termos da busca' :
             filterMode === 'my-folders' ? 'Crie sua primeira pasta para organizar seus arquivos' : 
             filterMode === 'accessible' ? 'Você ainda não tem acesso a nenhuma pasta' : 
             'Não há pastas disponíveis no momento'}
          </p>
          {filterMode !== 'accessible' && (
            <ActionButton onClick={handleCreateFolder}>
              {FiPlus({ size: 18 })}
              Criar Primeira Pasta
            </ActionButton>
          )}
        </EmptyState>
      ) : (
        <>
          <FoldersContainer viewMode={viewMode}>
            {paginatedFolders.map((folder) => (
                          <FolderCard 
                key={folder.id} 
                viewMode={viewMode}
                onDoubleClick={() => handleOpenFolder(folder)}
                title="Duplo clique para abrir a pasta"
              >
              <FolderIcon>
                {FiFolder({ size: viewMode === 'grid' ? 48 : 24 })}
              </FolderIcon>
              <FolderInfo>
                <FolderName>{folder.name}</FolderName>
                <FolderDetails>
                  {folder.fileCount} arquivo{folder.fileCount !== 1 ? 's' : ''}
                  <br />
                  Criada em {new Date(folder.createdAt).toLocaleDateString('pt-BR')}
                </FolderDetails>
              </FolderInfo>
                              <FolderActions>
                  <ActionIcon 
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation();
                      handleOpenFolder(folder);
                    }}
                    title="Abrir pasta"
                    primary
                  >
                    {FiFolder({ size: 16 })}
                  </ActionIcon>
                  {canUploadToFolder(folder) && (
                    <ActionIcon 
                      onClick={(e: React.MouseEvent) => {
                        e.stopPropagation();
                        document.getElementById(`upload-${folder.id}`)?.click();
                      }}
                      title="Upload arquivo"
                      success
                    >
                      {FiUpload({ size: 16 })}
                    </ActionIcon>
                  )}
                  {canEditFolder(folder) && (
                    <ActionIcon 
                      onClick={(e: React.MouseEvent) => {
                        e.stopPropagation();
                        handleEditFolder(folder);
                      }}
                      title="Editar pasta"
                    >
                      {FiEdit2({ size: 16 })}
                    </ActionIcon>
                  )}
                  {canManagePermissions(folder) && (
                    <ActionIcon 
                      onClick={(e: React.MouseEvent) => {
                        e.stopPropagation();
                        handleManagePermissions(folder);
                      }}
                      title="Gerenciar permissões"
                    >
                      {FiUsers({ size: 16 })}
                    </ActionIcon>
                  )}
                  {canDeleteFolder(folder) && (
                    <ActionIcon 
                      onClick={(e: React.MouseEvent) => {
                        e.stopPropagation();
                        handleDeleteFolder(folder);
                      }}
                      title="Excluir pasta"
                      danger
                    >
                      {FiTrash2({ size: 16 })}
                    </ActionIcon>
                  )}
                </FolderActions>
                
                {/* Input de upload invisível para cada pasta - apenas se tiver permissão */}
                {canUploadToFolder(folder) && (
                  <input
                    id={`upload-${folder.id}`}
                    type="file"
                    multiple
                    accept=".txt,.docx,.pdf,.png,.jpg,.jpeg"
                    onChange={(e) => handleQuickUpload(folder.id, e.target.files)}
                    style={{ display: 'none' }}
                  />
                )}
            </FolderCard>
          ))}
        </FoldersContainer>

        {/* Paginação */}
        {totalPages > 1 && (
          <PaginationContainer>
            <PaginationInfo>
              Mostrando {startIndex + 1}-{Math.min(endIndex, filteredFolders.length)} de {filteredFolders.length} pastas
            </PaginationInfo>
            <PaginationButtons>
              <PaginationButton
                disabled={currentPage === 1}
                onClick={() => handlePageChange(currentPage - 1)}
              >
                Anterior
              </PaginationButton>
              
              {generatePageNumbers().map((page) => (
                <PaginationButton
                  key={page}
                  active={currentPage === page}
                  onClick={() => handlePageChange(page)}
                >
                  {page}
                </PaginationButton>
              ))}
              
              <PaginationButton
                disabled={currentPage === totalPages}
                onClick={() => handlePageChange(currentPage + 1)}
              >
                Próxima
              </PaginationButton>
            </PaginationButtons>
          </PaginationContainer>
        )}
        </>
      )}

      {/* Modal Criar/Editar Pasta */}
      {(modalType === 'create' || modalType === 'edit') && (
        <Modal>
          <ModalContent>
            <ModalHeader>
              <ModalTitle>
                {modalType === 'create' ? 'Nova Pasta' : 'Editar Pasta'}
              </ModalTitle>
              <ModalCloseButton onClick={handleCloseModal}>×</ModalCloseButton>
            </ModalHeader>
            <ModalBody>
              <FormGroup>
                <FormLabel htmlFor="name">Nome da Pasta</FormLabel>
                <FormInput
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Digite o nome da pasta"
                  autoFocus
                />
              </FormGroup>
              <FormGroup>
                <FormLabel htmlFor="description">Descrição (opcional)</FormLabel>
                <FormInput
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Digite uma descrição para a pasta"
                />
              </FormGroup>
            </ModalBody>
            <ModalFooter>
              <CancelButton onClick={handleCloseModal}>Cancelar</CancelButton>
              <ConfirmButton 
                onClick={handleSubmitForm}
                disabled={!formData.name.trim()}
              >
                {modalType === 'create' ? 'Criar' : 'Salvar'}
              </ConfirmButton>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}

      {/* Modal Excluir Pasta */}
      {modalType === 'delete' && selectedFolder && (
        <Modal>
          <ModalContent>
            <ModalHeader>
              <ModalTitle>Excluir Pasta</ModalTitle>
              <ModalCloseButton onClick={handleCloseModal}>×</ModalCloseButton>
            </ModalHeader>
            <ModalBody>
              <p>
                Tem certeza que deseja excluir a pasta <strong>"{selectedFolder.name}"</strong>?
              </p>
              <p style={{ color: theme.error, fontSize: '14px' }}>
                Esta ação não pode ser desfeita. Todos os arquivos dentro da pasta também serão excluídos.
              </p>
            </ModalBody>
            <ModalFooter>
              <CancelButton onClick={handleCloseModal}>Cancelar</CancelButton>
              <ConfirmButton onClick={handleSubmitForm} style={{ backgroundColor: theme.error }}>
                Excluir
              </ConfirmButton>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}

      {/* Modal Gerenciar Permissões */}
      <PermissionsModal
        folder={selectedFolder}
        isOpen={modalType === 'permissions' && !!selectedFolder}
        onClose={handleCloseModal}
        onUpdate={loadFolders}
      />
    </Container>
  );
};

export default Folders; 